import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import {
  TrendingUp, AlertTriangle, Trophy, Calendar, Swords,
  Plus, Pencil, Trash2, Loader2, Zap, ArrowUp, Wand2, Globe, Search,
  Send, FileText, Eye, Brain, X, ArrowRight, Settings, BarChart3,
  ShoppingCart, ThumbsUp, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { SyncHealthByScraper } from "@/components/tender360/SyncHealthByScraper";
import { LatestOpportunities } from "@/components/tender360/LatestOpportunities";
import NewsQuarantinePanel from "@/components/tender360/NewsQuarantinePanel";

/** Formato europeo €/% */
const fmtEUR = (n) => Number(n || 0).toLocaleString("es-ES", { maximumFractionDigits: 0 }) + " €";
// Iter307 · G06 — normaliza fechas ISO crudas a DD/MM/AAAA (es-ES) en pantalla.
const fmtFechaOferta = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
};
const fmtPct = (n) => Number(n || 0).toFixed(1) + "%";
const fmtInt = (n) => Number(n || 0).toLocaleString("es-ES");

const KPI_COLORS = {
  pipeline:   "from-purple-600 to-indigo-600",
  win:        "from-green-600 to-emerald-600",
  forecast:   "from-blue-600 to-cyan-600",
  alerts:     "from-red-600 to-rose-600",
  ai_conv:    "from-fuchsia-600 to-pink-600",
};

export default function CuadroMando({ focusExternalId } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoDetecting, setAutoDetecting] = useState(false);
  const [showCompetitorForm, setShowCompetitorForm] = useState(false);
  const [editingComp, setEditingComp] = useState(null);
  const [compForm, setCompForm] = useState({ name: "", cif: "", won_count: 0, notes: "" });
  const [competitors, setCompetitors] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [sharing, setSharing] = useState(false);
  const [externalOps, setExternalOps] = useState([]);
  const [externalDG, setExternalDG] = useState("all");           // SERVICIOS | TRATAMIENTO | all
  const [externalActividad, setExternalActividad] = useState("all"); // C&W | GA | MOB | Light Capex | PPP
  const [externalMinScore, setExternalMinScore] = useState(60);
  const [externalMinMm, setExternalMinMm] = useState(0); // Iter283 · filtro de importe persistente
  const [externalOnlyLots, setExternalOnlyLots] = useState(false); // NEW: filtro multi-lote
  const [scoringPending, setScoringPending] = useState(false);
  const [funnel, setFunnel] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [aiConfig, setAiConfig] = useState({ convert_threshold: 80, hot_threshold: 85, active_areas: [], llm_provider: "anthropic", llm_model: "claude-sonnet-4-6" });
  const [savingConfig, setSavingConfig] = useState(false);
  const [aiPrompt, setAiPrompt] = useState({ prompt: "", is_custom: false, default_prompt: "" });
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [mercadoTopIA, setMercadoTopIA] = useState([]);
  const [gapWinner, setGapWinner] = useState(null);
  const [roi, setRoi] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [accuracyFull, setAccuracyFull] = useState(null);
  const [accThreshold, setAccThreshold] = useState(70);
  const [competitorNews, setCompetitorNews] = useState({ items: [], total: 0 });
  const [scrapingNews, setScrapingNews] = useState(false);
  const [newsFilter, setNewsFilter] = useState({ competitor: "all", category: "all" });
  const [showQuarantine, setShowQuarantine] = useState(false);
  const [llmOptions, setLlmOptions] = useState({});
  const [promptHistory, setPromptHistory] = useState([]);
  const [testingPrompt, setTestingPrompt] = useState(false);
  const [promptTestResults, setPromptTestResults] = useState(null);

  // Scroll a la oportunidad focada (cuando se llega desde email Premium)
  useEffect(() => {
    if (!focusExternalId || !externalOps?.length) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-ext-id="${CSS.escape(focusExternalId)}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [focusExternalId, externalOps]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 30, min_score: externalMinScore, include_raw: "true" });
      if (externalDG !== "all") params.append("direccion_general", externalDG);
      if (externalActividad !== "all") params.append("actividad", externalActividad);
      if (externalOnlyLots) params.append("has_lots", "true");
      if (externalMinMm) params.append("min_mm", externalMinMm);
      const [dash, comps, ext, fun, roiData, accData, accFull] = await Promise.all([
        axios.get(`${API}/tender360/m0/cuadro-mando`),
        axios.get(`${API}/tender360/m0/competitors`),
        axios.get(`${API}/tender360/m0/external-opportunities?${params.toString()}`),
        axios.get(`${API}/tender360/m0/external-opportunities/conversion-funnel?months=6`),
        axios.get(`${API}/tender360/m0/external-opportunities/scorer-roi`),
        axios.get(`${API}/tender360/m0/external-opportunities/scorer-accuracy`),
        axios.get(`${API}/tender360/m0/external-opportunities/scorer-accuracy-full?threshold=${accThreshold}`),
      ]);
      setData(dash.data);
      setCompetitors(comps.data || []);
      setExternalOps(ext.data || []);
      setFunnel(fun.data);
      setRoi(roiData.data);
      setAccuracy(accData.data);
      setAccuracyFull(accFull.data);
      // Cargar noticias competidores (no-bloqueante: si falla, lista vacía)
      try {
        const newsParams = new URLSearchParams({ limit: 60 });
        if (newsFilter.competitor !== "all") newsParams.append("competitor", newsFilter.competitor);
        if (newsFilter.category !== "all") newsParams.append("category", newsFilter.category);
        const newsResp = await axios.get(`${API}/competitor-news?${newsParams}`);
        setCompetitorNews(newsResp.data || { items: [], total: 0 });
      } catch { /* ignore */ }
      // Top-5 oportunidades MERCADO por Score Pando (top of mind)
      try {
        const topIa = await axios.get(`${API}/mercado/top-ia?limit=5`);
        setMercadoTopIA(topIa.data?.items || []);
      } catch { /* ignore */ }
      // Gap de puntuación vs Winner
      try {
        const gap = await axios.get(`${API}/ofertas-comercial/apertura/gap-winner`);
        setGapWinner(gap.data);
      } catch { /* ignore */ }
    } catch (e) {
      toast.error("Error cargando cuadro de mando");
    } finally {
      setLoading(false);
    }
  }, [externalDG, externalActividad, externalMinScore, externalMinMm, externalOnlyLots, accThreshold, newsFilter]);
  useEffect(() => { reload(); }, [reload]);

  // Reset actividad si cambia DG y la actividad ya no es válida
  useEffect(() => {
    if (externalDG === "all") return;
    const ACT_BY_DG = { "SERVICIOS": ["C&W","GA","MOB"], "TRATAMIENTO": ["Light Capex","PPP"] };
    const allowed = ACT_BY_DG[externalDG] || [];
    if (externalActividad !== "all" && !allowed.includes(externalActividad)) setExternalActividad("all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalDG]);

  // ── Config Pando ──────────────────────────────────────────────────────────
  const openConfigModal = async () => {
    try {
      const [cfg, pr, llms, hist] = await Promise.all([
        axios.get(`${API}/tender360/m0/ai-scorer/config`),
        axios.get(`${API}/tender360/m0/ai-scorer/prompt`),
        axios.get(`${API}/tender360/m0/ai-scorer/llm-options`),
        axios.get(`${API}/tender360/m0/ai-scorer/prompt/history`),
      ]);
      setAiConfig(cfg.data);
      setAiPrompt(pr.data);
      setLlmOptions(llms.data || {});
      setPromptHistory(hist.data || []);
      setPromptTestResults(null);
      setShowConfigModal(true);
    } catch (e) {
      toast.error("No se pudo cargar la config Pando");
    }
  };
  const toggleArea = (area) => {
    setAiConfig(c => ({
      ...c,
      active_areas: c.active_areas.includes(area)
        ? c.active_areas.filter(a => a !== area)
        : [...c.active_areas, area],
    }));
  };
  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const { data } = await axios.put(`${API}/tender360/m0/ai-scorer/config`, aiConfig);
      toast.success(`Config Pando guardada · convertir≥${data.config.convert_threshold} · hot≥${data.config.hot_threshold}`);
      setShowConfigModal(false);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando");
    } finally {
      setSavingConfig(false);
    }
  };
  const savePrompt = async () => {
    setSavingPrompt(true);
    try {
      await axios.put(`${API}/tender360/m0/ai-scorer/prompt`, { prompt: aiPrompt.prompt });
      toast.success(`Prompt actualizado (${aiPrompt.prompt.length} chars). Re-evalúa con "Pre-seleccionar Pando" para aplicar.`);
      setAiPrompt(p => ({ ...p, is_custom: true }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando prompt");
    } finally {
      setSavingPrompt(false);
    }
  };

  const triggerCompetitorScrape = async () => {
    setScrapingNews(true);
    try {
      const { data } = await axios.post(`${API}/competitor-news/scrape-now-blocking`);
      toast.success(`Scraping OK · ${data.total_new} noticias nuevas`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error scrapeando competidores");
    } finally {
      setScrapingNews(false);
    }
  };
  const quarantineNews = async (id) => {
    try {
      await axios.post(`${API}/competitor-news/${id}/quarantine`);
      setCompetitorNews((prev) => ({ ...prev, items: prev.items.filter((x) => x.id !== id), total: Math.max(0, (prev.total || 1) - 1) }));
      toast.success("Noticia ocultada (reversible desde la traza)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo ocultar la noticia");
    }
  };
  const resetPrompt = async () => {
    if (!window.confirm("¿Restaurar el prompt por defecto? Se perderán los cambios actuales.")) return;
    try {
      await axios.post(`${API}/tender360/m0/ai-scorer/prompt/reset`);
      const { data } = await axios.get(`${API}/tender360/m0/ai-scorer/prompt`);
      setAiPrompt(data);
      toast.success("Prompt restaurado al default");
    } catch (e) {
      toast.error("Error reseteando");
    }
  };

  const testPrompt = async () => {
    setTestingPrompt(true);
    setPromptTestResults(null);
    try {
      const { data } = await axios.post(`${API}/tender360/m0/ai-scorer/prompt/test`, { prompt: aiPrompt.prompt });
      setPromptTestResults(data.samples);
      toast.success("Prompt probado con 3 muestras");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error probando prompt");
    } finally {
      setTestingPrompt(false);
    }
  };

  const rollbackPrompt = async (history_id) => {
    if (!window.confirm("¿Restaurar esta versión histórica como prompt activo?")) return;
    try {
      await axios.post(`${API}/tender360/m0/ai-scorer/prompt/rollback`, { history_id });
      const [pr, hist] = await Promise.all([
        axios.get(`${API}/tender360/m0/ai-scorer/prompt`),
        axios.get(`${API}/tender360/m0/ai-scorer/prompt/history`),
      ]);
      setAiPrompt(pr.data);
      setPromptHistory(hist.data || []);
      toast.success("Versión restaurada");
    } catch (e) {
      toast.error("Error en rollback");
    }
  };


  const exportFunnel = (format) => {
    const url = `${API}/tender360/m0/external-opportunities/conversion-funnel/export?format=${format}&months=6`;
    axios.get(url, { responseType: "blob" })
      .then(({ data }) => {
        const blob = new Blob([data]);
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = `funnel-conversion-ia-${new Date().toISOString().slice(0, 10)}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
        toast.success(`Funnel exportado a ${format.toUpperCase()}`);
      })
      .catch(() => toast.error("Error exportando"));
  };

  const triggerScorePending = async () => {
    setScoringPending(true);
    try {
      const { data } = await axios.post(`${API}/tender360/m0/external-opportunities/score-pending?limit=200`);
      toast.success(data.message || "Scoring Pando en marcha");
      setTimeout(reload, 30000);  // recargar tras 30s para ver resultados
    } catch (e) {
      toast.error("No se pudo lanzar el scoring");
    } finally {
      setScoringPending(false);
    }
  };

  const triggerIntegration = async (slug, label) => {
    try {
      await axios.post(`${API}/tender360/m0/integrations/${slug}/sync`);
      toast.success(`${label}: sincronización completada`);
      reload();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.warning(`${label} pendiente de configurar`, { description: detail.slice(0, 180) });
    }
  };

  const autoDetectComps = async () => {
    setAutoDetecting(true);
    try {
      const { data } = await axios.post(`${API}/tender360/m0/competitors/auto-detect`);
      toast.success(`Auto-detect: +${data.added} nuevos · ${data.updated} actualizados (${data.total_winners_found} adjudicatarios analizados)`);
      reload();
    } catch (e) {
      toast.error("Error en auto-detect: " + (e.response?.data?.detail || e.message));
    } finally {
      setAutoDetecting(false);
    }
  };

  const submitCompetitor = async () => {
    if (!compForm.name.trim()) return toast.error("Nombre obligatorio");
    try {
      if (editingComp) {
        await axios.put(`${API}/tender360/m0/competitors/${editingComp.id}`, compForm);
        toast.success("Competidor actualizado");
      } else {
        await axios.post(`${API}/tender360/m0/competitors`, compForm);
        toast.success("Competidor creado");
      }
      setShowCompetitorForm(false);
      setEditingComp(null);
      setCompForm({ name: "", cif: "", won_count: 0, notes: "" });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando");
    }
  };

  const deleteCompetitor = async (c) => {
    if (!window.confirm(`¿Eliminar competidor «${c.name}»?`)) return;
    try {
      await axios.delete(`${API}/tender360/m0/competitors/${c.id}`);
      toast.success("Eliminado");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error eliminando");
    }
  };

  // ── Compartir resumen ejecutivo ─────────────────────────────────────────
  const openShareModal = async () => {
    setShowShareModal(true);
    try {
      const { data } = await axios.get(`${API}/tender360/m0/executive-summary/recipients`);
      setRecipients(data.recipients || []);
    } catch (e) {
      toast.error("No se pudo cargar la lista de destinatarios");
    }
  };

  const previewPdf = () => {
    axios.get(`${API}/tender360/m0/executive-summary/preview`, { responseType: "blob" })
      .then(({ data }) => {
        const url = URL.createObjectURL(data);
        window.open(url, "_blank");
      })
      .catch(() => toast.error("No se pudo generar el PDF"));
  };

  const sendNow = async () => {
    setSharing(true);
    try {
      const { data } = await axios.post(`${API}/tender360/m0/executive-summary/send`);
      const ok = data.results?.filter(r => r.ok).length || 0;
      toast.success(`Resumen enviado a ${ok}/${data.recipients_count} destinatarios (${data.pdf_size_kb} KB)`);
      setShowShareModal(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error enviando");
    } finally {
      setSharing(false);
    }
  };

  // Edición de destinatarios desde el modal
  const [newRecipientEmail, setNewRecipientEmail] = useState("");
  const [newRecipientName, setNewRecipientName] = useState("");
  const saveRecipients = async (updated) => {
    try {
      const { data } = await axios.put(`${API}/tender360/m0/executive-summary/recipients`, { recipients: updated });
      setRecipients(data.recipients);
      toast.success(`Lista actualizada (${data.count} destinatarios)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando destinatarios");
    }
  };
  const addRecipient = () => {
    const email = newRecipientEmail.trim().toLowerCase();
    if (!email.includes("@")) return toast.error("Email inválido");
    if (recipients.some(r => r.email === email)) return toast.error("Ya está en la lista");
    const updated = [...recipients, { email, name: newRecipientName.trim() || email.split("@")[0] }];
    saveRecipients(updated);
    setNewRecipientEmail(""); setNewRecipientName("");
  };
  const removeRecipient = (email) => {
    saveRecipients(recipients.filter(r => r.email !== email));
  };

  // Conversión externa → tender interno
  const convertToTender = async (op, targetState = "identificada") => {
    const targetLabel = { identificada: "identificada", estudio: "a estudio", decidir: "por decidir", publicada: "publicada" }[targetState];
    if (!window.confirm(`¿Convertir esta oportunidad en licitación con estado "${targetLabel}"?\n\n${op.title?.slice(0, 100)}`)) return;
    try {
      // Iter 122 fix: opp_id ahora va en body (K8s ingress rechazaba %2F en path)
      const { data } = await axios.post(
        `${API}/tender360/m0/external-opportunities/convert-to-tender`,
        { opp_id: op.external_id, target_state: targetState }
      );
      toast.success(`Convertida a licitación · estado: ${data.target_label} · prob_win ${Math.round((data.tender?.prob_win || 0) * 100)}%`, {
        description: `ID: ${data.tender_id?.slice(0, 8)}…`,
      });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo convertir");
    }
  };

  // Importar N ofertas (una por lote) en ofertas_comercial
  const importByLots = async (op) => {
    const n = op?.raw?.lots?.length || 0;
    if (n < 2) return;
    const totalLote = op.raw.lots.reduce((s, l) => s + (l.value_eur || 0), 0);
    const ok = window.confirm(
      `¿Crear ${n} ofertas comerciales (una por lote) a partir de esta licitación?\n\n` +
      op.raw.lots.map(l => `  · Lote ${l.id_lote}: ${l.name?.slice(0,50) || ''} — ${(l.value_eur||0).toLocaleString('es-ES')} €`).join("\n") +
      `\n\nTotal lotes: ${totalLote.toLocaleString('es-ES')} € · Estado inicial BORRADOR.`
    );
    if (!ok) return;
    try {
      const { data } = await axios.post(`${API}/ofertas-comercial/import-from-external-by-lots`, {
        external_id: op.external_id,
      });
      const created = data?.created || 0;
      const skipped = (data?.skipped || []).length;
      if (created > 0) {
        toast.success(`📥 ${created} oferta(s) creada(s) en BORRADOR` + (skipped ? ` · ${skipped} duplicado(s) saltado(s)` : ""), {
          description: `Revísalas en /ofertas-comercial · estado BORRADOR`,
        });
      } else {
        toast.info(`Todas las ofertas ya existían (${skipped} duplicados)`);
      }
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudieron crear las ofertas por lote");
    }
  };

  if (loading || !data) {
    return <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{[...Array(4)].map((_, i) => <div key={i} className="h-32 rounded-xl loading-shimmer"/>)}</div>;
  }

  const { kpis, sources } = data;

  return (
    <div className="space-y-5" data-testid="m0-cuadro-mando">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs text-gray-500">
          Resumen ejecutivo agregado de <strong>{fmtInt(sources.licitaciones_count)}</strong> licitaciones,{" "}
          <strong>{fmtInt(sources.ofertas_count)}</strong> ofertas y <strong>{fmtInt(sources.historic_count)}</strong> históricos · actualizado a las {data.computed_at?.slice(11, 16)} UTC
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={openShareModal}
            data-testid="m0-share-btn"
            className="text-[11px] px-3 py-1.5 rounded-lg bg-gradient-to-br from-[#007934] to-emerald-600 text-white hover:opacity-90 flex items-center gap-1.5 font-semibold shadow-sm"
            title="Genera PDF de 1 página y lo envía por email al Comité Comercial"
          >
            <Send size={11}/> Compartir resumen
          </button>
          <button onClick={reload} className="text-[11px] text-[#007934] hover:underline flex items-center gap-1">
            <Loader2 size={11} className={loading ? "animate-spin" : ""}/> Refrescar
          </button>
        </div>
      </div>

      {/* ── 4 KPIs principales ───────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="m0-kpis">
        <KpiCard icon={TrendingUp} color={KPI_COLORS.pipeline} testId="kpi-pipeline"
          label="Pipeline ponderado"
          big={fmtEUR(kpis.pipeline.weighted_eur)}
          sub={`Total ${fmtEUR(kpis.pipeline.total_eur)} · ${kpis.pipeline.active_opportunities} oportunidades activas`}/>
        <KpiCard icon={Trophy} color={KPI_COLORS.win} testId="kpi-winrate"
          label="Win rate (12 meses)"
          big={fmtPct(kpis.win_rate.pct)}
          sub={`✓ ${kpis.win_rate.won_12m} ganadas · ✗ ${kpis.win_rate.lost_12m} perdidas · ${fmtEUR(kpis.win_rate.value_won_12m_eur)} adjudicado`}/>
        <KpiCard icon={Calendar} color={KPI_COLORS.forecast} testId="kpi-forecast"
          label="Forecast trimestral"
          big={fmtEUR(kpis.forecast_quarter.value_eur)}
          sub={`${kpis.forecast_quarter.count} oportunidades antes de ${fmtFechaOferta(kpis.forecast_quarter.quarter_end)}`}/>
        <KpiCard icon={AlertTriangle} color={KPI_COLORS.alerts} testId="kpi-alerts"
          label="Alertas activas"
          big={fmtInt(kpis.alerts.total)}
          sub={kpis.alerts.high_count > 0 ? `${kpis.alerts.high_count} críticas · revisa abajo` : "Sin alertas críticas"}/>
        <KpiCard icon={Brain} color={KPI_COLORS.ai_conv} testId="kpi-ai-conversion"
          label="Conversión Pando"
          big={fmtPct(kpis.ai_conversion?.conversion_rate_pct ?? 0)}
          sub={`${fmtInt(kpis.ai_conversion?.converted_total ?? 0)}/${fmtInt(kpis.ai_conversion?.high_score_total ?? 0)} ≥${kpis.ai_conversion?.convert_threshold ?? 80} · 30d: ${fmtPct(kpis.ai_conversion?.conversion_rate_30d_pct ?? 0)}`}/>
      </div>

      {/* ── Top 5 MERCADO por Score Pando ─────────────────────────────────── */}
      {mercadoTopIA.length > 0 && (
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4" data-testid="widget-mercado-top-ia">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="text-sm font-bold text-emerald-900 flex items-center gap-1.5">
              <ShoppingCart size={14}/> Top-of-mind · MERCADO pendiente de decisión
              <span className="text-[10px] text-emerald-700 font-normal">Mayor Score Pando · aún sin pasar a ESTUDIO</span>
            </h3>
            <a
              href="/ofertas-comercial?tab=mercado"
              className="text-[10px] px-2 py-1 rounded bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-100 font-semibold inline-flex items-center gap-1"
              data-testid="widget-mercado-open">
              Ver todo MERCADO <ArrowRight size={10}/>
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
            {mercadoTopIA.map(m => (
              <div key={m.id} className="bg-white border border-emerald-200 rounded-lg p-2.5 hover:shadow-md transition-shadow" data-testid={`widget-top-ia-${m.id}`}>
                <div className="flex items-start justify-between gap-1 mb-1.5">
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded inline-flex items-center gap-0.5 ${
                    m.ai_recommendation === "GO" ? "bg-green-100 text-green-700" :
                    "bg-amber-100 text-amber-700"
                  }`}>
                    {m.ai_recommendation === "GO" ? <ThumbsUp size={8}/> : <Sparkles size={8}/>}
                    {m.ai_recommendation} · {m.ai_score}
                  </span>
                  <span className="text-[9px] bg-slate-100 text-slate-600 px-1 py-0.5 rounded font-mono">{m.plataforma_licitacion_electronica || "—"}</span>
                </div>
                <p className="text-[11px] font-semibold text-gray-900 line-clamp-2" title={m.objeto_contrato}>
                  {m.objeto_contrato || "—"}
                </p>
                <p className="text-[10px] text-gray-600 line-clamp-1 mt-0.5" title={m.cliente}>
                  {m.cliente || "—"} {m.provincia && `· ${m.provincia}`}
                </p>
                {m.ai_summary && (
                  <p className="text-[9px] text-gray-500 mt-1.5 line-clamp-2 italic">"{m.ai_summary}"</p>
                )}
                <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-gray-100">
                  <span className="text-[9px] text-gray-500">
                    {m.presupuesto ? new Intl.NumberFormat("es-ES",{style:"currency",currency:"EUR",maximumFractionDigits:0}).format(m.presupuesto) : "—"}
                  </span>
                  <span className="text-[9px] text-gray-400">{m.fecha_presentacion ? fmtFechaOferta(m.fecha_presentacion) : "sin fecha"}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Gap de puntuación vs Winner ────────────────────────────────── */}
      {gapWinner && gapWinner.samples_count > 0 && (
        <div className="bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-200 rounded-xl p-4" data-testid="widget-gap-winner">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="text-sm font-bold text-indigo-900 flex items-center gap-1.5">
              <Swords size={14}/> Gap de puntuación vs Winner
              <span className="text-[10px] text-indigo-700 font-normal">· muestra de {gapWinner.samples_count} aperturas</span>
            </h3>
          </div>

          {/* Overall */}
          {gapWinner.overall && gapWinner.overall.count > 0 && (
            <div className="grid grid-cols-3 gap-2 mb-3">
              <GapPill label="Gap TOT" value={gapWinner.overall.gap_tot} vsm={gapWinner.overall.vsm_avg_tot} winner={gapWinner.overall.win_avg_tot}/>
              <GapPill label="Gap TEC" value={gapWinner.overall.gap_tec} vsm={gapWinner.overall.vsm_avg_tec} winner={gapWinner.overall.win_avg_tec}/>
              <GapPill label="Gap ECO" value={gapWinner.overall.gap_eco} vsm={gapWinner.overall.vsm_avg_eco} winner={gapWinner.overall.win_avg_eco}/>
            </div>
          )}

          {/* Por DG y Actividad */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {gapWinner.by_dg?.length > 0 && (
              <div className="bg-white rounded-lg border border-indigo-100 p-3">
                <h4 className="text-[10px] font-bold uppercase text-indigo-800 mb-2">Por Dirección General</h4>
                <div className="space-y-1">
                  {gapWinner.by_dg.map(d => <GapRow key={d.key} row={d}/>)}
                </div>
              </div>
            )}
            {gapWinner.by_actividad?.length > 0 && (
              <div className="bg-white rounded-lg border border-indigo-100 p-3">
                <h4 className="text-[10px] font-bold uppercase text-indigo-800 mb-2">Por Actividad</h4>
                <div className="space-y-1">
                  {gapWinner.by_actividad.map(d => <GapRow key={d.key} row={d}/>)}
                </div>
              </div>
            )}
          </div>

          <p className="text-[10px] text-indigo-700 mt-2 italic">
            Gap negativo = VSM por debajo del Winner. Gap positivo = VSM supera al Winner (lo ideal).
            Interpretación rápida: si Gap TEC &lt; Gap ECO perdemos por técnica; si Gap ECO &lt; Gap TEC perdemos por precio.
          </p>
        </div>
      )}
      {funnel && (
        <div className="bg-gradient-to-br from-fuchsia-50/40 to-purple-50/40 border border-fuchsia-100 rounded-xl p-4" data-testid="m0-funnel">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="text-sm font-bold text-fuchsia-900 flex items-center gap-1.5">
              <BarChart3 size={14}/> Funnel de Conversión Pando · últimos {funnel.period_months} meses
              <span className="text-[10px] text-fuchsia-700 font-normal">umbral score ≥ {funnel.convert_threshold}</span>
            </h3>
            <div className="flex items-center gap-1">
              <button
                onClick={() => exportFunnel("csv")}
                data-testid="funnel-export-csv"
                className="text-[10px] px-2 py-1 rounded border border-fuchsia-300 bg-white text-fuchsia-700 hover:bg-fuchsia-100 flex items-center gap-1 font-semibold"
                title="Descargar funnel como CSV"
              >
                <FileText size={10}/> CSV
              </button>
              <button
                onClick={() => exportFunnel("xlsx")}
                data-testid="funnel-export-xlsx"
                className="text-[10px] px-2 py-1 rounded border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50 flex items-center gap-1 font-semibold"
                title="Descargar funnel como Excel"
              >
                <FileText size={10}/> XLSX
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3" data-testid="funnel-totals">
            <FunnelCell label="Descubiertas" value={funnel.totals.discovered} color="bg-purple-500"/>
            <FunnelCell label={`Score ≥ ${funnel.convert_threshold}`} value={funnel.totals.high_score} color="bg-fuchsia-500"/>
            <FunnelCell label="Convertidas" value={funnel.totals.converted} color="bg-emerald-500"/>
            <FunnelCell label="En oferta" value={funnel.totals.in_offer} color="bg-blue-500"/>
            <FunnelCell
              label={`Acierto scoring · ${roi?.decided ?? 0} decididas`}
              value={(roi?.decided ?? 0) > 0 && roi?.win_rate_pct != null ? `${roi.win_rate_pct}%` : "N/D"}
              color="bg-amber-500"
              testId="funnel-roi"
              hint={roi ? ((roi.decided ?? 0) > 0 ? `${roi.won} ganadas · ${roi.lost} perdidas · score medio ${roi.avg_score}` : "Sin oportunidades decididas todavía: no hay denominador (no es 0%, es N/D)") : ""}
            />
          </div>
          {funnel.by_month.length > 0 && (
            <div className="bg-white rounded-lg p-3 border border-fuchsia-100">
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">Evolución mensual</p>
              <div className="space-y-1.5">
                {funnel.by_month.map(m => (
                  <div key={m.month} className="flex items-center gap-2 text-[11px]">
                    <span className="w-16 text-gray-500 font-mono">{m.month}</span>
                    <div className="flex-1 flex gap-1 items-center">
                      <div className="flex-1 bg-gray-100 rounded h-2 overflow-hidden relative">
                        <div className="h-full bg-purple-500" style={{ width: `${Math.min(100, m.discovered / Math.max(1, Math.max(...funnel.by_month.map(x => x.discovered))) * 100)}%` }}/>
                      </div>
                      <span className="w-12 text-right text-gray-600">{m.discovered}</span>
                      <span className="w-12 text-right text-fuchsia-700 font-semibold">{m.high_score}</span>
                      <span className="w-12 text-right text-emerald-700 font-semibold">{m.converted}</span>
                      <span className="w-14 text-right text-gray-700">{fmtPct(m.conversion_rate)}</span>
                    </div>
                  </div>
                ))}
                <div className="flex items-center gap-2 text-[9px] uppercase tracking-wider text-gray-400 pt-1.5 border-t border-gray-100">
                  <span className="w-16"></span>
                  <span className="flex-1 flex gap-1">
                    <span className="flex-1"></span>
                    <span className="w-12 text-right">Descub.</span>
                    <span className="w-12 text-right">Score+</span>
                    <span className="w-12 text-right">Conv.</span>
                    <span className="w-14 text-right">Tasa</span>
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Competitor News (Inteligencia competidores · scraper híbrido) ── */}
      <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="m0-competitor-news">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
            <Swords size={14} className="text-rose-600"/> Inteligencia de Competidores · Noticias scrapeadas
            <span className="text-[10px] text-gray-400 font-normal">{competitorNews.total} items</span>
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={newsFilter.competitor} onChange={e => setNewsFilter(f => ({...f, competitor: e.target.value}))}
                    className="text-xs border border-gray-200 rounded px-2 py-1 bg-white" data-testid="news-filter-competitor">
              <option value="all">Todos</option>
              {(competitorNews.sources || []).map(s => (
                <option key={s.slug} value={s.slug}>{s.competitor}</option>
              ))}
            </select>
            <select value={newsFilter.category} onChange={e => setNewsFilter(f => ({...f, category: e.target.value}))}
                    className="text-xs border border-gray-200 rounded px-2 py-1 bg-white" data-testid="news-filter-category">
              <option value="all">Todas</option>
              <option value="tender_won">🏆 Adjudicaciones ganadas</option>
              <option value="tender_lost">❌ No adjudicada</option>
              <option value="presentation">🎤 Presentaciones</option>
              <option value="news">📰 Noticias</option>
            </select>
            <button onClick={triggerCompetitorScrape} disabled={scrapingNews}
                    className="text-xs bg-rose-600 text-white px-3 py-1 rounded hover:bg-rose-700 disabled:opacity-50 flex items-center gap-1"
                    data-testid="news-scrape-btn">
              {scrapingNews ? <Loader2 size={11} className="animate-spin"/> : <Globe size={11}/>}
              Scrapear ahora
            </button>
            <button onClick={() => setShowQuarantine(true)}
                    className="text-xs bg-white border border-gray-300 text-gray-600 px-3 py-1 rounded hover:border-amber-400 hover:text-amber-700 flex items-center gap-1"
                    data-testid="news-review-hidden-btn">
              <Eye size={11}/> Revisar ocultas
            </button>
          </div>
        </div>
        {competitorNews.items.length === 0 ? (
          <p className="text-xs text-gray-400 italic py-3 text-center">
            Sin noticias scrapeadas todavía. Pulsa <strong>Scrapear ahora</strong> para arrancar la primera ronda.
          </p>
        ) : (
          <div className="space-y-1.5 max-h-80 overflow-y-auto">
            {competitorNews.items.slice(0, 30).map(n => (
              <div key={n.id} className="flex items-start gap-2 p-2 hover:bg-gray-50 rounded text-xs border border-gray-50" data-testid={`news-${n.id}`}>
                <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold flex-shrink-0 ${
                  n.category === "tender_won" ? "bg-amber-100 text-amber-700" :
                  n.category === "tender_lost" ? "bg-rose-100 text-rose-700" :
                  n.category === "presentation" ? "bg-purple-100 text-purple-700" :
                  "bg-gray-100 text-gray-600"
                }`}>{n.category === "tender_won" ? "🏆 GANÓ" : n.category === "tender_lost" ? "❌ PERDIÓ" : n.category === "presentation" ? "🎤 PRES" : "📰"}</span>
                <span className="text-[9px] bg-rose-50 text-rose-700 px-1.5 py-0.5 rounded font-bold uppercase flex-shrink-0">{n.competitor.split(" ")[0]}</span>
                <div className="flex-1 min-w-0">
                  <a href={n.source_url} target="_blank" rel="noreferrer" className="font-semibold text-gray-800 hover:text-rose-700 line-clamp-2" title={n.title}>
                    {n.title}
                  </a>
                  {n.summary && <p className="text-[10px] text-gray-500 line-clamp-1 mt-0.5">{n.summary}</p>}
                </div>
                <span className="text-[9px] text-gray-400 flex-shrink-0">{fmtFechaOferta(n.scraped_at)}</span>
                <button onClick={() => quarantineNews(n.id)} data-testid={`news-quarantine-${n.id}`}
                  title="No pertinente: ocultar del feed (reversible)"
                  className="text-[9px] text-gray-300 hover:text-rose-600 flex-shrink-0 px-1">✕</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {showQuarantine && (
        <NewsQuarantinePanel
          onClose={() => setShowQuarantine(false)}
          onRestored={reload}
        />
      )}

      {/* ── Pando Accuracy: ¿La Pando acierta? ─────────────────────────────────── */}      {accuracyFull && accuracyFull.n > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="m0-accuracy">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
              <Brain size={14} className="text-amber-600"/> ¿La Pando acierta? · Pando vs Decisión Humana
              <span className="text-[10px] text-gray-400 font-normal">
                {accuracyFull.n} muestras · licit={accuracyFull.sources.licitaciones} · ofertas={accuracyFull.sources.ofertas_comercial}
              </span>
            </h3>
            <div className="flex items-center gap-1.5 text-xs">
              <label className="text-gray-500">Threshold Pando:</label>
              <input type="range" min="0" max="100" step="5" value={accThreshold}
                     onChange={e => setAccThreshold(parseInt(e.target.value))}
                     className="accent-amber-500" data-testid="acc-threshold-slider"/>
              <span className="font-bold text-amber-700 w-8">{accThreshold}</span>
            </div>
          </div>

          {/* Métricas globales */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
            {[
              { k: "auc", label: "ROC AUC", v: accuracyFull.auc, fmt: "n", color: "amber" },
              { k: "precision", label: "Precision", v: accuracyFull.metrics.precision, fmt: "p", color: "blue" },
              { k: "recall", label: "Recall", v: accuracyFull.metrics.recall, fmt: "p", color: "indigo" },
              { k: "f1", label: "F1 score", v: accuracyFull.metrics.f1, fmt: "n", color: "purple" },
              { k: "accuracy", label: "Accuracy", v: accuracyFull.metrics.accuracy, fmt: "p", color: "green" },
            ].map(m => (
              <div key={m.k} className={`rounded-lg p-2 border bg-${m.color}-50 border-${m.color}-200`} data-testid={`acc-metric-${m.k}`}>
                <p className={`text-[9px] uppercase tracking-wider font-semibold text-${m.color}-700`}>{m.label}</p>
                <p className="text-xl font-bold text-gray-900 font-['Outfit']">
                  {m.fmt === "p" ? `${(m.v * 100).toFixed(1)}%` : m.v.toFixed(3)}
                </p>
              </div>
            ))}
          </div>

          {/* Confusion matrix + buckets */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div className="bg-gray-50/60 rounded-lg p-3 border border-gray-100">
              <p className="text-[10px] uppercase font-bold text-gray-500 mb-2">Matriz de Confusión @ thr={accThreshold}</p>
              <div className="grid grid-cols-3 text-[10px]">
                <div></div>
                <div className="text-center font-bold text-gray-600">Humano: GO</div>
                <div className="text-center font-bold text-gray-600">Humano: NO-GO</div>
                <div className="font-bold text-gray-600 self-center">Pando: GO</div>
                <div className="bg-green-100 text-green-800 font-bold text-center py-1.5 rounded" data-testid="cm-tp">
                  {accuracyFull.confusion_matrix.tp} TP
                </div>
                <div className="bg-red-100 text-red-700 font-bold text-center py-1.5 rounded" data-testid="cm-fp">
                  {accuracyFull.confusion_matrix.fp} FP
                </div>
                <div className="font-bold text-gray-600 self-center">Pando: NO-GO</div>
                <div className="bg-orange-100 text-orange-700 font-bold text-center py-1.5 rounded" data-testid="cm-fn">
                  {accuracyFull.confusion_matrix.fn} FN
                </div>
                <div className="bg-emerald-100 text-emerald-700 font-bold text-center py-1.5 rounded" data-testid="cm-tn">
                  {accuracyFull.confusion_matrix.tn} TN
                </div>
              </div>
            </div>

            <div className="md:col-span-2 bg-gray-50/60 rounded-lg p-3 border border-gray-100">
              <p className="text-[10px] uppercase font-bold text-gray-500 mb-2">Hit-rate por tramo de score</p>
              <div className="grid grid-cols-5 gap-1.5">
                {accuracyFull.buckets.map(b => (
                  <div key={b.bucket} className="bg-white rounded p-2 border border-gray-100" data-testid={`accuracy-bucket-${b.bucket}`}>
                    <p className="text-[9px] uppercase text-gray-400 font-semibold">Score {b.bucket}</p>
                    <p className="text-base font-bold text-gray-900 font-['Outfit']">{b.win_rate_pct}%</p>
                    <p className="text-[9px] text-gray-500">{b.won}✓ / {b.lost}✗ · {b.total} ops</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ROC curve points + Monthly evolution */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div className="bg-gray-50/60 rounded-lg p-3 border border-gray-100">
              <p className="text-[10px] uppercase font-bold text-gray-500 mb-2">Curva ROC (puntos cada 5)</p>
              <svg viewBox="0 0 100 100" className="w-full h-32 bg-white border border-gray-100 rounded">
                {/* Diagonal random reference */}
                <line x1="0" y1="100" x2="100" y2="0" stroke="#E5E7EB" strokeWidth="0.5" strokeDasharray="2,2"/>
                {/* ROC line */}
                {accuracyFull.roc.length > 0 && (
                  <polyline
                    fill="none"
                    stroke="#F59E0B"
                    strokeWidth="1.5"
                    points={[...accuracyFull.roc]
                      .sort((a, b) => a.fpr - b.fpr)
                      .map(p => `${(p.fpr * 100).toFixed(1)},${(100 - p.tpr * 100).toFixed(1)}`)
                      .join(" ")}
                  />
                )}
                {/* Threshold marker */}
                {(() => {
                  const cur = accuracyFull.roc.find(p => p.threshold === accThreshold);
                  if (!cur) return null;
                  return <circle cx={cur.fpr * 100} cy={100 - cur.tpr * 100} r="2" fill="#DC2626"/>;
                })()}
              </svg>
              <p className="text-[9px] text-gray-400 mt-1">FPR (eje X) vs TPR (eje Y) · ⚪ thr actual · AUC = {accuracyFull.auc}</p>
            </div>

            <div className="bg-gray-50/60 rounded-lg p-3 border border-gray-100">
              <p className="text-[10px] uppercase font-bold text-gray-500 mb-2">Evolución mensual @ thr={accThreshold}</p>
              {accuracyFull.monthly_series.length === 0 ? (
                <p className="text-[10px] text-gray-400 italic py-3 text-center">Aún sin histórico mensual.</p>
              ) : (
                <table className="w-full text-[10px]">
                  <thead>
                    <tr className="text-gray-500 border-b border-gray-100">
                      <th className="text-left py-1">Mes</th>
                      <th className="text-right">N</th>
                      <th className="text-right">Precision</th>
                      <th className="text-right">Recall</th>
                      <th className="text-right">Acc</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {accuracyFull.monthly_series.map(m => (
                      <tr key={m.month} className="hover:bg-white">
                        <td className="py-0.5 font-mono">{m.month}</td>
                        <td className="text-right text-gray-500">{m.n}</td>
                        <td className="text-right font-bold text-blue-700">{(m.precision * 100).toFixed(0)}%</td>
                        <td className="text-right font-bold text-indigo-700">{(m.recall * 100).toFixed(0)}%</td>
                        <td className="text-right font-bold text-green-700">{(m.accuracy * 100).toFixed(0)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <p className={`text-[11px] font-medium px-3 py-2 rounded-lg ${
            accuracyFull.auc >= 0.85 ? "bg-green-50 text-green-700 border border-green-200" :
            accuracyFull.auc >= 0.70 ? "bg-amber-50 text-amber-700 border border-amber-200" :
            accuracyFull.auc >= 0.55 ? "bg-orange-50 text-orange-700 border border-orange-200" :
            "bg-red-50 text-red-700 border border-red-200"
          }`} data-testid="acc-interpretation">{accuracyFull.interpretation}</p>
        </div>
      )}

      {/* ── Top 3 oportunidades + Alertas ────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="m0-top3">
          <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-1.5">
            <ArrowUp size={14} className="text-purple-600"/> Top 3 oportunidades activas
          </h3>
          <div className="space-y-2">
            {kpis.top3_opportunities.length === 0 ? (
              <p className="text-xs text-gray-400 italic py-3">Sin oportunidades activas con valor estimado.</p>
            ) : kpis.top3_opportunities.map((o, i) => (
              <div key={o.id || i} className="flex items-start gap-3 p-2 bg-gray-50/50 rounded border border-gray-100" data-testid={`top3-${i}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${i === 0 ? "bg-amber-100 text-amber-700" : i === 1 ? "bg-gray-200 text-gray-700" : "bg-orange-100 text-orange-700"}`}>{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate" title={o.title}>{o.title}</p>
                  <p className="text-[11px] text-gray-500 truncate">{o.client || "—"} · <span className="bg-white border border-gray-200 px-1 rounded text-[10px]">{o.source}</span></p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-bold text-purple-700">{fmtEUR(o.value)}</p>
                  <p className="text-[10px] text-gray-500">{fmtPct(o.prob_win * 100)} prob.</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="m0-alerts">
          <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-1.5">
            <AlertTriangle size={14} className="text-red-600"/> Alertas
            {kpis.alerts.high_count > 0 && <span className="text-[10px] font-bold uppercase bg-red-100 text-red-700 px-1.5 py-0.5 rounded">{kpis.alerts.high_count} críticas</span>}
          </h3>
          <div className="space-y-1.5 max-h-[320px] overflow-y-auto">
            {kpis.alerts.items.length === 0 ? (
              <p className="text-xs text-gray-400 italic py-3">Sin alertas. Buena señal: no hay deadlines inminentes ni ofertas estancadas.</p>
            ) : kpis.alerts.items.map((a, i) => (
              <div key={i} className={`flex items-start gap-2 p-2 rounded text-[11px] border ${a.severity === "high" ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`} data-testid={`alert-${i}`}>
                <AlertTriangle size={12} className={`flex-shrink-0 mt-0.5 ${a.severity === "high" ? "text-red-600" : "text-amber-600"}`}/>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-800 truncate" title={a.title}>{a.title}</p>
                  <p className="text-gray-500 truncate">
                    {a.type === "deadline_close" ? `⏰ ${a.days_left} días · deadline ${a.deadline}` :
                     a.type === "stalled_ai_converted" ? `🤖 Pando convertida · ${a.days_stalled}d en identificada · score ${a.ai_score}` :
                     `💤 estancada desde ${a.last_update}`}
                    {" · "}
                    <span className="bg-white border border-gray-200 px-1 rounded text-[9px]">{a.source}</span>
                  </p>
                </div>
                <span className="text-[10px] font-bold text-gray-700 flex-shrink-0">{fmtEUR(a.value)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Ranking competidores ─────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="m0-competitors">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
            <Swords size={14} className="text-orange-600"/> Ranking competidores
            <span className="text-[10px] text-gray-400 font-normal">({competitors.length} registrados)</span>
          </h3>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={autoDetectComps}
              disabled={autoDetecting}
              data-testid="m0-auto-detect"
              className="text-[11px] px-3 py-1.5 rounded-lg border border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100 flex items-center gap-1.5 disabled:opacity-50"
              title="Analiza historic_tenders.winner para detectar adjudicatarios automáticamente"
            >
              {autoDetecting ? <Loader2 size={11} className="animate-spin"/> : <Wand2 size={11}/>} Auto-detectar
            </button>
            <button
              onClick={() => { setEditingComp(null); setCompForm({ name: "", cif: "", won_count: 0, notes: "" }); setShowCompetitorForm(true); }}
              data-testid="m0-add-competitor"
              className="text-[11px] px-3 py-1.5 rounded-lg bg-[#007934] text-white hover:bg-[#006528] flex items-center gap-1.5"
            >
              <Plus size={11}/> Añadir
            </button>
          </div>
        </div>
        {competitors.length === 0 ? (
          <p className="text-xs text-gray-400 italic py-4 text-center">Aún no hay competidores. Pulsa <strong>Auto-detectar</strong> para analizar histórico, o <strong>Añadir</strong> para introducirlos manualmente.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase text-gray-400 tracking-wider border-b border-gray-100">
                  <th className="text-left py-2 pr-3">#</th>
                  <th className="text-left py-2 pr-3">Competidor</th>
                  <th className="text-right py-2 pr-3">Ganadas</th>
                  <th className="text-right py-2 pr-3">Perdidas</th>
                  <th className="text-right py-2 pr-3">€ adjudicados</th>
                  <th className="text-left py-2 pr-3">Origen</th>
                  <th className="text-left py-2">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {competitors.map((c, i) => (
                  <tr key={c.id} className="hover:bg-gray-50" data-testid={`competitor-row-${c.id}`}>
                    <td className="py-2 pr-3 font-bold text-gray-500">{i + 1}</td>
                    <td className="py-2 pr-3">
                      <div className="font-medium text-gray-900">{c.name}</div>
                      {c.cif && <div className="text-[10px] text-gray-400">{c.cif}</div>}
                      {c.notes && <div className="text-[10px] text-gray-500 italic line-clamp-1" title={c.notes}>{c.notes}</div>}
                    </td>
                    <td className="py-2 pr-3 text-right font-bold text-green-700">{fmtInt(c.won_count)}</td>
                    <td className="py-2 pr-3 text-right text-gray-500">{fmtInt(c.lost_count)}</td>
                    <td className="py-2 pr-3 text-right text-gray-700">{c.auto_value_won_eur ? fmtEUR(c.auto_value_won_eur) : "—"}</td>
                    <td className="py-2 pr-3">
                      {c.auto_detected
                        ? <span className="text-[9px] font-bold uppercase bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Auto</span>
                        : <span className="text-[9px] font-bold uppercase bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">Manual</span>}
                    </td>
                    <td className="py-2 flex gap-1">
                      <button onClick={() => { setEditingComp(c); setCompForm({ name: c.name, cif: c.cif || "", won_count: c.won_count || 0, notes: c.notes || "" }); setShowCompetitorForm(true); }} className="p-1 text-gray-400 hover:text-[#007934]" data-testid={`comp-edit-${c.id}`}>
                        <Pencil size={11}/>
                      </button>
                      <button onClick={() => deleteCompetitor(c)} className="p-1 text-gray-300 hover:text-red-600" data-testid={`comp-delete-${c.id}`}>
                        <Trash2 size={11}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Oportunidades externas pre-seleccionadas por Pando ─────────────── */}
      <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="m0-external-ops">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
            <Brain size={14} className="text-purple-600"/> Oportunidades externas pre-seleccionadas por Pando
            <span className="text-[10px] text-gray-400 font-normal">({externalOps.length} mostradas)</span>
            <button
              onClick={openConfigModal}
              data-testid="ai-config-btn"
              title="Configurar umbrales y áreas Pando"
              className="ml-1 p-1 text-gray-300 hover:text-purple-600 transition-colors"
            >
              <Settings size={12}/>
            </button>
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={externalDG}
              onChange={e => setExternalDG(e.target.value)}
              data-testid="ext-dg-filter"
              className="text-[11px] border border-gray-200 rounded-lg px-2 py-1.5 bg-white font-semibold"
              title="Dirección General"
            >
              <option value="all">Toda DG</option>
              <option value="SERVICIOS">DG Servicios</option>
              <option value="TRATAMIENTO">DG Tratamiento</option>
            </select>
            <select
              value={externalActividad}
              onChange={e => setExternalActividad(e.target.value)}
              data-testid="ext-actividad-filter"
              className="text-[11px] border border-gray-200 rounded-lg px-2 py-1.5 bg-white"
              title={externalDG === "all" ? "Actividad (todas las DG)" : `Actividades de ${externalDG}`}
            >
              <option value="all">Toda actividad</option>
              {(externalDG === "all"
                ? ["C&W","GA","MOB","Light Capex","PPP"]
                : (externalDG === "SERVICIOS" ? ["C&W","GA","MOB"] : ["Light Capex","PPP"])
              ).map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <select
              value={externalMinScore}
              onChange={e => setExternalMinScore(parseInt(e.target.value, 10))}
              data-testid="ext-score-filter"
              className="text-[11px] border border-gray-200 rounded-lg px-2 py-1.5 bg-white"
            >
              <option value={80}>Score ≥ 80 (top)</option>
              <option value={70}>Score ≥ 70</option>
              <option value={60}>Score ≥ 60</option>
              <option value={50}>Score ≥ 50</option>
              <option value={0}>Todas (sin filtro)</option>
            </select>
            {/* Iter283 · Importe ≥ X M€ — filtro persistente pedido por el usuario */}
            <div
              data-testid="cm-filter-importe-wrap"
              title="Filtra oportunidades externas con importe por encima del umbral"
              className={`inline-flex items-center gap-1.5 border rounded-lg px-2 py-1 text-[11px] ${
                externalMinMm > 0 ? "border-sky-300 bg-sky-50" : "border-gray-200 bg-white"
              }`}>
              <span className={externalMinMm > 0 ? "font-semibold text-sky-700" : "text-gray-500"}>Importe ≥</span>
              <input
                type="range" min={0} max={30} step={1}
                value={externalMinMm}
                onChange={(e) => setExternalMinMm(Number(e.target.value))}
                data-testid="cm-filter-importe"
                className="accent-sky-500 w-20"
              />
              <span className={`font-mono w-12 text-right ${externalMinMm > 0 ? "text-sky-700 font-bold" : "text-gray-500"}`}>{externalMinMm}M€</span>
            </div>
            <label
              data-testid="ext-only-lots-toggle"
              title="Solo licitaciones con múltiples lotes (≥ 2)"
              className={`text-[11px] flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border cursor-pointer select-none transition-colors ${
                externalOnlyLots
                  ? "border-purple-500 bg-purple-100 text-purple-800 font-semibold"
                  : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              <input
                type="checkbox"
                checked={externalOnlyLots}
                onChange={e => setExternalOnlyLots(e.target.checked)}
                data-testid="ext-only-lots-checkbox"
                className="h-3 w-3 accent-purple-600"
              />
              Solo con lotes
            </label>
            <button
              onClick={triggerScorePending}
              disabled={scoringPending}
              data-testid="ext-score-pending"
              className="text-[11px] px-3 py-1.5 rounded-lg border border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100 flex items-center gap-1.5 disabled:opacity-50"
              title="Re-evalúa con Claude Sonnet 4.6 las oportunidades sin score"
            >
              {scoringPending ? <Loader2 size={11} className="animate-spin"/> : <Brain size={11}/>} Pre-seleccionar Pando
            </button>
          </div>
        </div>
        {externalOps.length === 0 ? (
          <p className="text-xs text-gray-400 italic py-3 text-center">
            No hay oportunidades con esos filtros. Sincroniza PLACSP/BOE/TED desde abajo y pulsa <strong>Pre-seleccionar Pando</strong>.
          </p>
        ) : (
          <div className="space-y-1.5 max-h-[420px] overflow-y-auto">
            {externalOps.map((o, i) => {
              const canConvert = (o.ai_score ?? 0) >= 80 && !o.converted_to_tender_id;
              return (
                <div
                  key={o.external_id || i}
                  data-testid={`ext-op-${i}`}
                  data-ext-id={o.external_id || ""}
                  className={`flex items-start gap-3 p-2 rounded border transition-all group ${
                    focusExternalId && o.external_id === focusExternalId
                      ? "border-purple-500 bg-purple-50 ring-2 ring-purple-300 animate-pulse"
                      : "border-gray-100 hover:bg-gray-50 hover:border-purple-200"
                  }`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    o.ai_score >= 85 ? "bg-green-100 text-green-700" :
                    o.ai_score >= 70 ? "bg-emerald-100 text-emerald-700" :
                    o.ai_score >= 50 ? "bg-amber-100 text-amber-700" :
                    "bg-gray-100 text-gray-500"
                  }`}>
                    {o.ai_score ?? "—"}
                  </div>
                  <a href={o.url || "#"} target="_blank" rel="noreferrer" className="flex-1 min-w-0 cursor-pointer">
                    <p className="text-[12.5px] font-medium text-gray-900 group-hover:text-purple-700 line-clamp-1" title={o.title}>
                      {o.is_new && (
                        <span
                          data-testid={`ext-new-badge-${i}`}
                          className="inline-block mr-1.5 align-middle px-1.5 py-0.5 rounded text-[9px] font-extrabold uppercase bg-yellow-300 text-yellow-900 animate-pulse shadow-sm ring-1 ring-yellow-500"
                          title="Detectada en las últimas 24h"
                        >
                          ★ NUEVO
                        </span>
                      )}
                      {Array.isArray(o?.raw?.lots) && o.raw.lots.length > 1 && (
                        <span
                          data-testid={`ext-lots-badge-${i}`}
                          className="inline-block mr-1.5 align-middle px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-indigo-100 text-indigo-700 ring-1 ring-indigo-300"
                          title={`Licitación por lotes (${o.raw.lots.length}): ` + (o.raw.lots.map(l => `${l.id_lote || '?'}: ${l.name || '?'} (${l.value_eur || '?'} €)`).join(' · '))}
                        >
                          ⊞ {o.raw.lots.length} lotes
                        </span>
                      )}
                      {o.title}
                    </p>
                    <p className="text-[10px] text-gray-500 mt-0.5">
                      <span className="bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase mr-1">{o.direccion_general || "—"}</span>
                      <span className="bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded text-[9px] font-bold mr-1">{o.actividad_comercial || "?"}</span>
                      {(() => {
                        const src = (o.source || "").toLowerCase();
                        const sourceStyles = {
                          placsp:  { cls: "bg-emerald-100 text-emerald-800", icon: "🇪🇸", label: "PLACSP" },
                          euskadi: { cls: "bg-rose-100 text-rose-800",       icon: "🟥", label: "Euskadi" },
                          xunta:   { cls: "bg-sky-100 text-sky-800",         icon: "🟢", label: "Xunta" },
                          boe:     { cls: "bg-amber-100 text-amber-800",     icon: "📜", label: "BOE" },
                          ted:     { cls: "bg-indigo-100 text-indigo-800",   icon: "🇪🇺", label: "TED" },
                        }[src] || { cls: "bg-gray-100 text-gray-600", icon: "", label: o.source || "?" };
                        return (
                          <span
                            className={`${sourceStyles.cls} px-1.5 py-0.5 rounded text-[9px] font-bold uppercase mr-1`}
                            data-testid={`ext-source-badge-${src || 'unknown'}`}
                            title={`Fuente: ${sourceStyles.label}`}
                          >
                            {sourceStyles.icon} {sourceStyles.label}
                          </span>
                        );
                      })()}
                      {o.organism && <span className="text-gray-400">· {o.organism.slice(0, 50)}</span>}
                      {o.publication_date && <span className="text-gray-400">· {o.publication_date}</span>}
                      {o.converted_to_tender_id && (
                        <span className="ml-2 bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase">✓ convertida</span>
                      )}
                    </p>
                    {o.ai_rationale && (
                      <p className="text-[10px] text-gray-500 italic mt-0.5 line-clamp-1" title={o.ai_rationale}>
                        🤖 {o.ai_rationale}
                      </p>
                    )}
                  </a>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {o.value_eur != null && (
                      <span className="text-[11px] font-bold text-gray-700">{fmtEUR(o.value_eur)}</span>
                    )}
                    {canConvert && (
                      <ConvertToTenderDropdown op={o} idx={i} onConvert={convertToTender} />
                    )}
                    {Array.isArray(o?.raw?.lots) && o.raw.lots.length > 1 && !o.lot_ofertas_ids?.length && (
                      <button
                        onClick={(e) => { e.stopPropagation(); importByLots(o); }}
                        data-testid={`ext-import-lots-${i}`}
                        className="text-[10px] px-2 py-1 rounded bg-indigo-50 border border-indigo-300 text-indigo-700 hover:bg-indigo-100 flex items-center gap-1 font-semibold whitespace-nowrap"
                        title={`Crear ${o.raw.lots.length} ofertas (una por lote) en ofertas_comercial`}
                      >
                        <Plus size={10}/> {o.raw.lots.length} ofertas
                      </button>
                    )}
                    {Array.isArray(o?.lot_ofertas_ids) && o.lot_ofertas_ids.length > 0 && (
                      <span
                        data-testid={`ext-lots-imported-${i}`}
                        className="text-[10px] px-2 py-1 rounded bg-indigo-100 border border-indigo-300 text-indigo-800 font-semibold whitespace-nowrap"
                        title={`Ya importadas ${o.lot_ofertas_ids.length} ofertas por lote`}
                      >
                        ✓ {o.lot_ofertas_ids.length} importadas
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Integraciones externas (activas) ─────────────────────────────── */}
      <div className="bg-emerald-50/30 border border-emerald-100 rounded-xl p-4" data-testid="m0-integrations">
        <h3 className="text-sm font-bold text-emerald-900 mb-2 flex items-center gap-1.5">
          <Globe size={14}/> Fuentes externas activas
          <span className="text-[9px] font-bold uppercase bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">EN PRODUCCIÓN</span>
        </h3>
        <p className="text-[11px] text-gray-600 mb-3">
          PLACSP, Euskadi y Xunta de Galicia se sincronizan <strong>automáticamente cada día a las 7:00</strong>. Las nuevas oportunidades pasan
          por el scorer Pando (Claude Sonnet 4.6) y aparecen filtradas arriba.
          Pulsa cada botón para forzar un sync manual.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <button onClick={() => triggerIntegration("placsp", "PLACSP")} data-testid="int-placsp"
            className="text-left p-3 rounded-lg bg-white border border-emerald-200 hover:border-emerald-400 transition-all">
            <p className="text-xs font-bold text-emerald-900 flex items-center gap-1.5"><Search size={11}/> 🇪🇸 PLACSP</p>
            <p className="text-[10px] text-gray-500 mt-1">Sector Público · ZIP mensual · diario 7:00</p>
          </button>
          <button onClick={() => triggerIntegration("euskadi", "Euskadi")} data-testid="int-euskadi"
            className="text-left p-3 rounded-lg bg-white border border-rose-200 hover:border-rose-400 transition-all">
            <p className="text-xs font-bold text-rose-900 flex items-center gap-1.5"><Search size={11}/> 🟥 Euskadi</p>
            <p className="text-[10px] text-gray-500 mt-1">API REST · diario 7:00</p>
          </button>
          <button onClick={() => triggerIntegration("xunta", "Xunta de Galicia")} data-testid="int-xunta"
            className="text-left p-3 rounded-lg bg-white border border-sky-200 hover:border-sky-400 transition-all">
            <p className="text-xs font-bold text-sky-900 flex items-center gap-1.5"><Search size={11}/> 🟢 Xunta</p>
            <p className="text-[10px] text-gray-500 mt-1">RSS · últimos 15 días · diario 7:00</p>
          </button>
          <button onClick={() => triggerIntegration("scraping", "Scraping competidores")} data-testid="int-scraping"
            className="text-left p-3 rounded-lg bg-white border border-emerald-200 hover:border-emerald-400 transition-all">
            <p className="text-xs font-bold text-emerald-900 flex items-center gap-1.5"><Zap size={11}/> Scraping</p>
            <p className="text-[10px] text-gray-500 mt-1">FCC · Urbaser · Prezero · Acciona · Paprec · Veolia</p>
          </button>
        </div>
        {/* Sync Health por scraper + Últimas 5 licitaciones · grid responsive */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <SyncHealthByScraper />
          <LatestOpportunities limit={5} />
        </div>
      </div>

      {/* ── Modal competidor ─────────────────────────────────────────────── */}
      {showCompetitorForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowCompetitorForm(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-5 space-y-3" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold flex items-center gap-2"><Swords size={14} className="text-orange-600"/> {editingComp ? "Editar" : "Nuevo"} competidor</h3>
            <input type="text" placeholder="Nombre *" value={compForm.name} onChange={e => setCompForm(f => ({ ...f, name: e.target.value }))}
                   className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="comp-form-name"/>
            <input type="text" placeholder="CIF (opcional)" value={compForm.cif} onChange={e => setCompForm(f => ({ ...f, cif: e.target.value }))}
                   className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"/>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-400">Adjudicaciones ganadas</label>
                <input type="number" min="0" value={compForm.won_count} onChange={e => setCompForm(f => ({ ...f, won_count: parseInt(e.target.value || 0, 10) }))}
                       className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"/>
              </div>
            </div>
            <textarea placeholder="Notas: tipos de servicios, regiones, fortalezas…" value={compForm.notes} onChange={e => setCompForm(f => ({ ...f, notes: e.target.value }))} rows={3}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"/>
            <div className="flex gap-2 justify-end pt-2">
              <button onClick={() => setShowCompetitorForm(false)} className="text-sm border border-gray-200 px-4 py-2 rounded-lg">Cancelar</button>
              <button onClick={submitCompetitor} className="text-sm bg-[#007934] text-white px-4 py-2 rounded-lg" data-testid="comp-form-submit">
                {editingComp ? "Guardar" : "Crear"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Modal Compartir resumen ejecutivo ────────────────────────────── */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowShareModal(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-5 space-y-4" onClick={e => e.stopPropagation()} data-testid="m0-share-modal">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-base font-bold flex items-center gap-2">
                  <Send size={16} className="text-[#007934]"/> Compartir Resumen Ejecutivo
                </h3>
                <p className="text-[11px] text-gray-500 mt-1">
                  PDF de 1 página con KPIs, Top-3 oportunidades y alertas.
                </p>
              </div>
            </div>

            <div className="bg-amber-50/50 border border-amber-200 rounded-lg p-3 text-[11px] text-amber-900">
              <p className="font-bold mb-1">📅 Envío automático cron</p>
              <p>Este PDF se envía automáticamente todos los <strong>lunes a las 8:15</strong> a la lista del Comité Comercial.</p>
            </div>

            <div>
              <p className="text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-1">
                Destinatarios ({recipients.length})
              </p>
              <div className="bg-gray-50 rounded-lg border border-gray-200 max-h-40 overflow-y-auto p-2 space-y-1" data-testid="recipients-list">
                {recipients.length === 0 ? (
                  <p className="text-[11px] text-gray-400 italic">Sin destinatarios configurados</p>
                ) : recipients.map((r, i) => (
                  <div key={r.email} className="flex items-center justify-between text-[11px] text-gray-700 group hover:bg-white rounded px-1.5 py-1">
                    <div>
                      <span className="font-medium">{r.name}</span>
                      <span className="text-gray-400 ml-2">{r.email}</span>
                    </div>
                    <button
                      onClick={() => removeRecipient(r.email)}
                      className="opacity-0 group-hover:opacity-100 p-1 text-gray-300 hover:text-red-600 transition-all"
                      data-testid={`recipient-remove-${i}`}
                      title="Quitar destinatario"
                    >
                      <X size={11}/>
                    </button>
                  </div>
                ))}
              </div>
              {/* Añadir destinatario */}
              <div className="flex gap-1.5 mt-2" data-testid="add-recipient-form">
                <input
                  type="email"
                  placeholder="email@valorizasm.com"
                  value={newRecipientEmail}
                  onChange={e => setNewRecipientEmail(e.target.value)}
                  className="flex-1 text-[11px] border border-gray-200 rounded px-2 py-1.5"
                  data-testid="add-recipient-email"
                />
                <input
                  type="text"
                  placeholder="Nombre"
                  value={newRecipientName}
                  onChange={e => setNewRecipientName(e.target.value)}
                  className="w-24 text-[11px] border border-gray-200 rounded px-2 py-1.5"
                  data-testid="add-recipient-name"
                />
                <button
                  onClick={addRecipient}
                  className="text-[11px] px-2 py-1.5 rounded bg-[#007934] text-white hover:bg-[#006528]"
                  data-testid="add-recipient-btn"
                >
                  <Plus size={11}/>
                </button>
              </div>
              <p className="text-[10px] text-gray-400 mt-1.5">
                Se persiste en BD <code className="bg-gray-100 px-1 rounded">app_settings.exec_summary_recipients</code>. El cron del lunes 8:15 usa esta lista.
              </p>
            </div>

            <div className="flex gap-2 justify-end pt-2 border-t border-gray-100">
              <button
                onClick={previewPdf}
                data-testid="m0-share-preview"
                className="text-sm border border-gray-200 px-3 py-2 rounded-lg flex items-center gap-1.5 hover:bg-gray-50"
              >
                <Eye size={13}/> Vista previa PDF
              </button>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-sm border border-gray-200 px-3 py-2 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={sendNow}
                disabled={sharing || recipients.length === 0}
                data-testid="m0-share-send"
                className="text-sm bg-[#007934] text-white px-4 py-2 rounded-lg flex items-center gap-1.5 hover:bg-[#006528] disabled:opacity-50"
              >
                {sharing ? <Loader2 size={13} className="animate-spin"/> : <Send size={13}/>}
                Enviar ahora
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Modal Config Pando Scorer ───────────────────────────────────────── */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowConfigModal(false)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full p-5 space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="ai-config-modal">
            <div>
              <h3 className="text-base font-bold flex items-center gap-2">
                <Settings size={16} className="text-purple-600"/> Configuración Scorer Pando
              </h3>
              <p className="text-[11px] text-gray-500 mt-1">
                Persistido en BD <code className="bg-gray-100 px-1 rounded">app_settings.ai_scorer_config</code>. Aplica al cron diario y a la UI.
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-bold text-gray-700 uppercase tracking-wider">Umbral CONVERTIR (botón "Convertir")</label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="range" min="0" max="100" step="5"
                    value={aiConfig.convert_threshold}
                    onChange={e => setAiConfig(c => ({ ...c, convert_threshold: parseInt(e.target.value, 10) }))}
                    className="flex-1"
                    data-testid="config-convert-threshold"
                  />
                  <span className="text-sm font-bold text-purple-700 w-10 text-right">{aiConfig.convert_threshold}</span>
                </div>
                <p className="text-[10px] text-gray-400">Solo aparece "Convertir" en oportunidades con score ≥ este umbral</p>
              </div>

              <div>
                <label className="text-[11px] font-bold text-gray-700 uppercase tracking-wider">Umbral HOT (email digest)</label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="range" min="0" max="100" step="5"
                    value={aiConfig.hot_threshold}
                    onChange={e => setAiConfig(c => ({ ...c, hot_threshold: parseInt(e.target.value, 10) }))}
                    className="flex-1"
                    data-testid="config-hot-threshold"
                  />
                  <span className="text-sm font-bold text-red-600 w-10 text-right">{aiConfig.hot_threshold}</span>
                </div>
                <p className="text-[10px] text-gray-400">Score ≥ este valor → email automático tras cada sync</p>
              </div>

              <div>
                <label className="text-[11px] font-bold text-gray-700 uppercase tracking-wider">Áreas activas (puntuables)</label>
                <div className="grid grid-cols-2 gap-1 mt-2" data-testid="config-areas">
                  {["residuos", "recogida", "contenerizacion", "transporte_residuos",
                    "limpieza_viaria", "movilidad", "zonas_verdes", "plantas_tratamiento"].map(area => (
                    <label key={area} className="flex items-center gap-1.5 text-[11px] text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={aiConfig.active_areas?.includes(area)}
                        onChange={() => toggleArea(area)}
                        data-testid={`config-area-${area}`}
                        className="accent-purple-600"
                      />
                      <span className="capitalize">{area.replace(/_/g, " ")}</span>
                    </label>
                  ))}
                </div>
                <p className="text-[10px] text-gray-400 mt-1.5">Si desactivas un área, las oportunidades de esa categoría siguen apareciendo pero el botón "Convertir" se inhibe.</p>
              </div>

              <div>
                <label className="text-[11px] font-bold text-gray-700 uppercase tracking-wider">Modelo LLM activo</label>
                <div className="flex gap-2 mt-1.5" data-testid="config-llm">
                  <select
                    value={aiConfig.llm_provider}
                    onChange={e => {
                      const p = e.target.value;
                      const firstModel = (llmOptions[p] || [])[0] || aiConfig.llm_model;
                      setAiConfig(c => ({ ...c, llm_provider: p, llm_model: firstModel }));
                    }}
                    className="text-[11px] border border-gray-200 rounded px-2 py-1.5 bg-white"
                    data-testid="config-llm-provider"
                  >
                    {(Object.keys(llmOptions).length > 0 ? Object.keys(llmOptions) : ["anthropic", "openai", "gemini"]).map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                  <select
                    value={aiConfig.llm_model}
                    onChange={e => setAiConfig(c => ({ ...c, llm_model: e.target.value }))}
                    className="flex-1 text-[11px] border border-gray-200 rounded px-2 py-1.5 bg-white"
                    data-testid="config-llm-model"
                  >
                    {(llmOptions[aiConfig.llm_provider] || [aiConfig.llm_model]).map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
                <p className="text-[10px] text-gray-400 mt-1">Cambio inmediato. Próximo "Pre-seleccionar Pando" usará este modelo.</p>
              </div>

              <div className="pt-3 border-t border-gray-100">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[11px] font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                    Prompt System (Claude)
                    {aiPrompt.is_custom && (
                      <span className="text-[9px] font-bold uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">PERSONALIZADO</span>
                    )}
                  </label>
                  <button
                    onClick={resetPrompt}
                    data-testid="prompt-reset"
                    className="text-[10px] text-gray-400 hover:text-red-600 underline"
                    title="Restaurar default"
                  >
                    reset default
                  </button>
                </div>
                <textarea
                  value={aiPrompt.prompt}
                  onChange={e => setAiPrompt(p => ({ ...p, prompt: e.target.value }))}
                  data-testid="prompt-textarea"
                  rows={6}
                  className="w-full text-[10px] font-mono border border-gray-200 rounded p-2 resize-y bg-gray-50 leading-tight"
                  placeholder="System message para Claude..."
                />
                <div className="flex items-center justify-between mt-1">
                  <p className="text-[10px] text-gray-400">
                    {aiPrompt.prompt?.length || 0} chars · re-evalúa con "Pre-seleccionar Pando" para aplicar
                  </p>
                  <div className="flex gap-1.5">
                    <button
                      onClick={testPrompt}
                      disabled={testingPrompt || !aiPrompt.prompt || aiPrompt.prompt.length < 50}
                      data-testid="prompt-test"
                      className="text-[10px] px-2 py-1 rounded border border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100 flex items-center gap-1 disabled:opacity-50 font-semibold"
                    >
                      {testingPrompt ? <Loader2 size={10} className="animate-spin"/> : <Eye size={10}/>} Probar
                    </button>
                    <button
                      onClick={savePrompt}
                      disabled={savingPrompt || !aiPrompt.prompt || aiPrompt.prompt.length < 50}
                      data-testid="prompt-save"
                      className="text-[10px] px-2 py-1 rounded bg-purple-600 text-white hover:bg-purple-700 flex items-center gap-1 disabled:opacity-50 font-semibold"
                    >
                      {savingPrompt ? <Loader2 size={10} className="animate-spin"/> : <Brain size={10}/>} Guardar
                    </button>
                  </div>
                </div>

                {/* Resultados del sandbox */}
                {promptTestResults && (
                  <div className="mt-2 bg-purple-50/60 border border-purple-200 rounded p-2 space-y-1" data-testid="prompt-test-results">
                    <p className="text-[10px] font-bold text-purple-700 uppercase">Resultados con 3 muestras</p>
                    {promptTestResults.map((r, i) => (
                      <div key={i} className="text-[10px] flex items-start gap-2 bg-white rounded p-1.5">
                        <span className={`w-7 h-7 flex-shrink-0 rounded flex items-center justify-center font-bold text-[11px] ${
                          r.score >= 80 ? "bg-green-100 text-green-700" :
                          r.score >= 50 ? "bg-amber-100 text-amber-700" :
                          "bg-gray-100 text-gray-500"
                        }`}>{r.score}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] text-gray-700 truncate" title={r.title}>{r.title}</p>
                          <p className="text-[9px] text-gray-500 italic line-clamp-1">{r.area} · {r.rationale}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Histórico de prompts (A/B testing) */}
                {promptHistory.length > 0 && (
                  <div className="mt-3 border-t border-gray-100 pt-2" data-testid="prompt-history">
                    <p className="text-[10px] font-bold text-gray-500 uppercase mb-1.5">Historial ({promptHistory.length})</p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {promptHistory.map(h => (
                        <div key={h.id} className="flex items-center gap-2 bg-gray-50 rounded p-1.5 text-[10px] hover:bg-gray-100">
                          <span className="text-gray-400 font-mono w-24">{h.archived_at?.slice(0, 16).replace("T", " ")}</span>
                          <span className="flex-1 truncate text-gray-600" title={h.prompt}>{h.prompt.slice(0, 80)}</span>
                          <button
                            onClick={() => rollbackPrompt(h.id)}
                            data-testid={`prompt-rollback-${h.id.slice(0, 8)}`}
                            className="text-[9px] text-purple-600 underline hover:text-purple-800 font-semibold"
                            title="Restaurar como activo"
                          >
                            usar
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-3 border-t border-gray-100">
              <button onClick={() => setShowConfigModal(false)} className="text-sm border border-gray-200 px-3 py-2 rounded-lg">Cancelar</button>
              <button
                onClick={saveConfig}
                disabled={savingConfig}
                data-testid="config-save"
                className="text-sm bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center gap-1.5 disabled:opacity-50"
              >
                {savingConfig ? <Loader2 size={13} className="animate-spin"/> : <Settings size={13}/>}
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, color, label, big, sub, testId }) {
  return (
    <div className={`rounded-xl text-white p-4 bg-gradient-to-br ${color}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <Icon size={18} className="opacity-80"/>
      </div>
      <p className="text-[10px] uppercase tracking-wider opacity-80 mb-0.5">{label}</p>
      <p className="text-2xl font-bold font-['Outfit'] truncate" title={big}>{big}</p>
      <p className="text-[10px] opacity-75 mt-1 line-clamp-2">{sub}</p>
    </div>
  );
}

function GapPill({ label, value, vsm, winner }) {
  const positive = value > 0;
  const color = value == null ? "bg-gray-50 text-gray-400"
              : positive ? "bg-emerald-100 text-emerald-800 border-emerald-200"
              : value > -5 ? "bg-amber-100 text-amber-800 border-amber-200"
              : "bg-rose-100 text-rose-800 border-rose-200";
  return (
    <div className={`rounded-lg border p-2.5 ${color}`}>
      <p className="text-[9px] uppercase font-semibold opacity-75">{label}</p>
      <p className="text-xl font-bold leading-none mt-1">
        {value != null ? (value > 0 ? "+" : "") + value.toFixed(1) : "—"}
      </p>
      <p className="text-[9px] opacity-80 mt-1">
        VSM {vsm?.toFixed(1) ?? "—"} · Winner {winner?.toFixed(1) ?? "—"}
      </p>
    </div>
  );
}

function GapRow({ row }) {
  const g = row.gap_tot ?? 0;
  const color = g >= 0 ? "bg-emerald-500"
              : g > -5 ? "bg-amber-500"
              : "bg-rose-500";
  const widthPct = Math.min(100, Math.abs(g) * 3);
  return (
    <div className="flex items-center gap-2 text-xs">
      <div className="w-24 font-semibold text-gray-700 truncate" title={row.key}>{row.key}</div>
      <div className="flex-1 flex items-center gap-2">
        <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden relative">
          <div className="absolute top-0 left-1/2 w-px h-full bg-gray-300"/>
          <div
            className={`${color} h-full absolute top-0`}
            style={g >= 0
              ? { left: "50%", width: `${widthPct/2}%` }
              : { right: "50%", width: `${widthPct/2}%` }}
          />
        </div>
        <span className={`font-mono font-bold text-[11px] w-12 text-right ${g >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
          {g > 0 ? "+" : ""}{g.toFixed(1)}
        </span>
        <span className="text-[9px] text-gray-400 w-10">n={row.count}</span>
      </div>
    </div>
  );
}


function FunnelCell({ label, value, color, testId, hint }) {
  return (
    <div className="bg-white rounded-lg p-3 border border-fuchsia-100" data-testid={testId} title={hint}>
      <div className={`w-2 h-8 ${color} rounded-full inline-block align-middle mr-2`}/>
      <div className="inline-block align-middle">
        <p className="text-[9px] uppercase tracking-wider text-gray-500 font-semibold">{label}</p>
        <p className="text-xl font-bold text-gray-900 font-['Outfit']">
          {typeof value === "number" ? value.toLocaleString("es-ES") : value}
        </p>
      </div>
    </div>
  );
}



/**
 * Iter 122: Dropdown con 3 destinos (a estudio / por decidir / publicada) para
 * convertir una oportunidad externa en licitación interna. Reemplaza al
 * botón único "Convertir" que solo iba a `identificada`.
 */
function ConvertToTenderDropdown({ op, idx, onConvert }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const OPTIONS = [
    { value: "estudio",   label: "A estudio",   emoji: "🔬", color: "bg-indigo-50 text-indigo-700 hover:bg-indigo-100", note: "prob_win 50%" },
    { value: "decidir",   label: "Por decidir", emoji: "❓", color: "bg-amber-50 text-amber-700 hover:bg-amber-100",     note: "prob_win 60%" },
    { value: "publicada", label: "Publicada",   emoji: "📢", color: "bg-blue-50 text-blue-700 hover:bg-blue-100",       note: "prob_win 70%" },
  ];

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        data-testid={`ext-convert-${idx}`}
        className="text-[10px] px-2 py-1 rounded bg-emerald-50 border border-emerald-300 text-emerald-700 hover:bg-emerald-100 flex items-center gap-1 font-semibold whitespace-nowrap"
        title="Convertir a licitación (elige destino)"
      >
        <ArrowRight size={10}/> Convertir ▾
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-30 overflow-hidden" data-testid={`ext-convert-menu-${idx}`}>
          {OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={(e) => { e.stopPropagation(); setOpen(false); onConvert(op, opt.value); }}
              data-testid={`ext-convert-${idx}-${opt.value}`}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left text-[11px] border-b border-gray-100 last:border-b-0 ${opt.color}`}
            >
              <span className="text-base">{opt.emoji}</span>
              <span className="font-semibold flex-1">{opt.label}</span>
              <span className="text-[9px] text-gray-500">{opt.note}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
